import { ChangeEvent, FormEvent, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { PUBLIC_ROUTES } from '../../constants/routeConstants';
import {
  VALIDATION_MESSAGES,
  REGEX_PATTERNS,
} from '../../utils/validationMessages';
import API_ENDPOINTS from '../../config/api';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import LoaderOverlay from '../../components/common/LoaderOverlay';
import { Input } from '../../components/ui/input';

interface ForgotPasswordData {
  email: string;
}

interface FormErrors {
  email?: string;
}

const ForgotPassword = () => {
  const [formData, setFormData] = useState<ForgotPasswordData>({
    email: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [uiOnlyMode] = useState(false);
  const navigate = useNavigate();

  const getBaseUrl = () => {
    if (typeof window !== 'undefined') {
      return `${window.location.origin}`;
    }
    return '';
  };

  const isFormValid =
    formData.email.trim() !== '' &&
    !errors.email &&
    REGEX_PATTERNS.email.test(formData.email);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.email) {
      newErrors.email = VALIDATION_MESSAGES.email.required;
    } else if (!REGEX_PATTERNS.email.test(formData.email)) {
      newErrors.email = VALIDATION_MESSAGES.email.invalid;
    } else if (formData.email.length > 100) {
      newErrors.email = VALIDATION_MESSAGES.email.maxLength;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        email: formData.email,
        baseUrl: getBaseUrl(),
      };

      if (uiOnlyMode) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        console.log('Forgot password payload:', payload);
        toast.success('Password reset link sent to your email! (UI Mode)');

        setTimeout(() => {
          navigate(PUBLIC_ROUTES.RESET_PASSWORD);
        }, 2000);
      } else {
        await axios.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, payload, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        toast.success('Password reset link sent to your email!');

        setTimeout(() => {
          navigate(PUBLIC_ROUTES.LOGIN);
        }, 2000);
      }
    } catch (error: any) {
      console.error('Forgot password error:', error);

      if (!uiOnlyMode) {
        let errorMessage = 'Failed to send password reset link';

        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.data?.message) {
          errorMessage = error.data.message;
        } else if (error.data?.error) {
          errorMessage = error.data.error;
        } else if (error.message) {
          errorMessage = error.message;
        }

        toast.error(errorMessage);
      } else {
        toast.error('Failed to send password reset link (UI Mode)');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    const newErrors: FormErrors = { ...errors };

    if (name === 'email') {
      if (!value) {
        newErrors.email = VALIDATION_MESSAGES.email.required;
      } else if (!REGEX_PATTERNS.email.test(value)) {
        newErrors.email = VALIDATION_MESSAGES.email.invalid;
      } else if (value.length > 100) {
        newErrors.email = VALIDATION_MESSAGES.email.maxLength;
      } else {
        newErrors.email = undefined;
      }
    }

    setErrors(newErrors);
  };

  const handleCancel = () => {
    navigate(PUBLIC_ROUTES.LOGIN);
  };

  return (
    <LoaderOverlay isLoading={loading} loadingText="Sending reset link...">
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Forgot Your Password?
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Enter your email address and we&apos;ll send you a link to reset
              your password.
            </p>
            <p className="mt-2 text-center text-sm text-gray-600">
              Or{' '}
              <Link
                to={PUBLIC_ROUTES.LOGIN}
                className="font-medium text-blue-600 hover:text-blue-500 px-2 py-1 rounded hover:bg-blue-50 transition-colors duration-200"
              >
                sign in to your account
              </Link>
            </p>
          </div>

          <div className="bg-white py-8 px-6 shadow rounded-lg">
            <form
              onSubmit={handleSubmit}
              noValidate
              className="space-y-6 text-left"
            >
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email address
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  className={`mt-1 block w-full px-3 py-1.5 border rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:shadow-lg ${
                    errors.email
                      ? 'border-red-500 bg-red-50 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-400 hover:border-gray-500 bg-white'
                  }`}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              <div className="flex space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={loading}
                  className="flex-1"
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  disabled={loading || !isFormValid}
                  className="flex-1 bg-blue-700 hover:bg-blue-800"
                >
                  Submit
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </LoaderOverlay>
  );
};

export default ForgotPassword;
